import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import DocumentTitle from '../components/DocumentTitle';
import Header from '../components/auth/Header';
import CompanySelect from '../components/inspection/CompanySelect';
import LocationSelect from '../components/inspection/LocationSelect';
import SublocationSelect from '../components/inspection/SublocationSelect';
import RemovalSelect from '../components/inspection/RemovalSelect';
import Tab from '../components/partials/Tab';
import PageLoader from '../components/partials/PageLoader';
import {getActiveInspection, setReportCheckFlag} from '../actions/inspections';
import { setCompanyTab, setLocationTab, setRemovalTab, setInspectionState, setInspectionStateAsync, resetInspectionTabTranslations } from '../actions/start-inspection';
import { updateUserDataRequest } from '../actions/auth';
import { useCertificateStore } from '../lib/stores/certificate';
import { uploadImages } from '../actions/inspections'
import moment from 'moment';
import _ from 'lodash';
import __ from '../helpers/translation';
import { SquareArrowOutUpRightIcon } from 'lucide-react'
import { logoutUser } from '@/actions/auth'
import { useSwipeable } from 'react-swipeable';
import api from "../actions/api";
import localforage from "localforage";
import { toast } from "react-toastify";
import { reportStatus, useScheduledReportStore } from '../lib/stores/scheduledReport';
import { useCreditStore } from '@/lib/stores/credit';
import { z } from 'zod';

export const Swipeable = ({children, ...props}) => {
  const handlers = useSwipeable(props);
  return (<div { ...handlers }>{children}</div>);
}

const propTypes = {
  inspection: PropTypes.object.isRequired,
  startInspection: PropTypes.object.isRequired,
  getActiveInspection: PropTypes.func.isRequired,
  setReportCheckFlag: PropTypes.func.isRequired,
  setCompanyTab: PropTypes.func.isRequired,
  setLocationTab: PropTypes.func.isRequired,
  setRemovalTab: PropTypes.func.isRequired,
  setInspectionState: PropTypes.func.isRequired,
  setInspectionStateAsync: PropTypes.func.isRequired,
  resetInspectionTabTranslations: PropTypes.func.isRequired,
  updateUserDataRequest: PropTypes.func.isRequired,
  logoutUser: PropTypes.func.isRequired,
};

class Inspection extends Component {

  constructor (props) {
    super(props);
    
    this.state = {
      leaveRight: false,
      leaveLeft: false,
      animate: false,
      hasStoredInspections: false,
      /**
       * This property is used to store the new terms and conditions that the user has to accept.
       * If `null`, no new terms have to be accepted. Else, this property will contain the new terms.
       * 
       * @type {{ id: number, title: string, url: string } | null}
       */
      newTermsToBeAccepted: null,
    }

    if(this.props.startInspection.currentTab == 'companies' && !this.props.startInspection.selectedCompany){
      this.props.resetInspectionTabTranslations();
    }

    this.selectCompany = this.selectCompany.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
    this.selectSublocation = this.selectSublocation.bind(this);
    this.selectRemoval = this.selectRemoval.bind(this);
    this.syncOfflineReports = this.syncOfflineReports.bind(this);

    this.removalSelectionRef = null;
  }

  //#region Inspection sync methods

    /**
     * This method syncs locally stored inspections with the server. 
     * It retrieves the stored inspections from local storage and uploads
     * them to the server. It also syncs uploaded images with the server.
     * 
     * @returns {Promise<void>} A promise that resolves when the inspections are successfully synced or rejects with an error message.
     */
    async syncOfflineReports() {

      if(!window.navigator.onLine) {
        toast.error(__('app.toasts.error_currently_offline'));
        return;
      }

      const resultPromise = Promise.all([
        this.syncCompletedReports(),
        this.syncStoppedReports()
      ])

      toast.promise(resultPromise, {
        pending: __('app.toasts.promise_offline_sync_pending'),
        success: __('app.toasts.promise_offline_sync_success'),
        error: {
          render({ data }){
            return <div>{data.message}</div>
          }
        }
      })
        .then(() => { 
          setTimeout(() => {
            window.location.reload();
          }, 1500)
        })
    }

    /**
     * This method syncs a completed report with the server. It uploads the report data and related images to the server.
     * 
     * @param {Object} report The report object to sync
     * @returns {Promise<void>} A promise that resolves when the report is successfully synced or rejects with an error message. 
     */
    async syncCompletedReports() {

      const { scheduledReports, removeReport } = useScheduledReportStore.getState();
      const { 
        credits,
        creditHandlers: { withdrawReservedCredits },
      } = useCreditStore.getState();
      const completedReports = scheduledReports
        .filter(report => report.status === reportStatus.COMPLETED_OFFLINE)
        .map(report => report.data);

      if(!completedReports.length) return;

      for (const report of completedReports) {
        try {

          const answerIds = report?.answers?.map(answer => answer?.id) || [];
          for(const answerId of answerIds) {
            await uploadImages(answerId, report.id);
          }
          console.log('All images uploaded successfully.');

          const creditSet = credits.find(credit => credit.companyId === report.relation_ids.company_id.toString());
          if(!creditSet) {
            throw new Error(`No credit set found for company with ID ${report.relation_ids.company_id}`);
          }

          // Withdraw reserved credits
          const withdrawnCredits = withdrawReservedCredits({ companyId: creditSet.companyId });

          const response = await window.axios.post(api.inspections.sync.replace(':report', report.id), {
            '_method': 'PATCH',
            emails: report.emails,
            action: 'complete',
            answers: report.answers,
            balanceToken: creditSet.balanceToken,
            creditsToDecrease: withdrawnCredits,
          })

          removeReport(report.id);
          console.log('Completed inspection synced successfully:', response.data);
        }
        catch(err) {
          toast.error(__('app.toasts.error_syncing_completed_inspection'));
          console.error('Error syncing completed inspection:', err);
          throw new Error('Error syncing completed inspection:', err);
        }
      }
    }

    async syncStoppedReports() {

      const { scheduledReports, removeReport } = useScheduledReportStore.getState();
      const stoppedReports = scheduledReports
        .filter(report => report.status === reportStatus.STOPPED)
        .map(report => report.data);

      if(!stoppedReports.length) return;

      for(const report of stoppedReports) {
        try {
          const answerIds = report?.answers?.map(answer => answer?.id) || [];
          for(const answerId of answerIds) {
            await uploadImages(answerId, report.id);
          }
          console.log('All images uploaded successfully.');

          const response = await window.axios.post(api.inspections.sync.replace(':report', report.id), {
            '_method': 'PATCH',
            action: 'stop',
            answers: report.answers,
            emails: report?.stopInfo?.emails || '',
            explanation: report?.stopInfo?.explanation || '',
            generate_report: report?.stopInfo?.generate_report || false
          });

          removeReport(report.id);
          console.log('Stopped inspection synced successfully:', response.data);
        }
        catch(err) {
          toast.error(__('app.toasts.error_syncing_stopped_inspection'));
          console.error('Error syncing stopped inspection:', err);
          throw new Error('Error syncing stopped inspection:', err);
        }
      }
    }

  //#endregion

  //#region Retrieval of certificates + Terms and conditions

    async syncCertificates() {
      try {

        const { 
          certificates,
          fetchAllCertificates,
          lastSync 
        } = useCertificateStore.getState();

        const lastSyncExpired = moment().isAfter(moment.unix(lastSync).add(5, 'minutes'));

        if(!certificates.length || lastSyncExpired) {
          await fetchAllCertificates();
        }

      }
      catch (error) {
        console.error('Error syncing certificates:', error);
      }
    }

    /**
     * This function is responsible for making the call to the server for checking if 
     * there are new terms to be accepted. If so, update the internal state with the new terms.
     */
    async checkForNewTerms() {
      if(!window.navigator.onLine) {
        return
      }

      const responseSchema = z.union([
        z.object({
          needs_acceptance: z.literal(true),
          new_terms: z.object({
            id: z.number().int().positive(),
            title: z.string(),
            url: z.string()
          })
        }),
        z.object({
          needs_acceptance: z.literal(false),
          new_terms: z.literal(null)
        })
      ])

      try {
        const response = await window.axios.get(api.terms.check);
        const parsed = responseSchema.safeParse(response.data);

        if(!parsed.success) {
          throw new Error('Invalid response from server:', parsed.error.format());
        }
        if(!parsed.data.needs_acceptance) return

        this.setState({ newTermsToBeAccepted: parsed.data.new_terms })
      }
      catch(err) {
        console.error('Error checking for new terms:', err);
      }
    }

    /**
     * Accepts new terms if available and the application is online.
     *
     * This asynchronous function checks if there are new terms to be accepted. If no terms are pending or if the application is offline,
     * it resets the internal state without performing any network request. When online and new terms are present, it makes a POST request
     * to accept the terms, logs the outcome, and resets the state afterward, regardless of success or failure.
     *
     * @async
     * @function acceptNewTerms
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    async acceptNewTerms() {

      if(!this.state.newTermsToBeAccepted) return;
      if(!window.navigator.onLine) {
        this.setState({ newTermsToBeAccepted: null });
        return;
      }

      try {
        const response = await window.axios.post(api.terms.accept.replace(':term', this.state.newTermsToBeAccepted.id));
        console.log('New terms accepted successfully:', response.data);
        this.setState({ newTermsToBeAccepted: null });

        setTimeout(() => {
          toast.success(__('app.terms.successfully_accepted'));
        }, 1000);
      }
      catch(err) {
        console.error('Error accepting new terms:', err);
        this.setState({ newTermsToBeAccepted: null });
      }
    }

    /**
     * This function is triggered when the user rejects the new terms and conditions.
     * With this action, the user will be logged out.
     */
    async rejectNewTerms() {
      this.props.logoutUser(() => {
        window.location.href = '/';
      })
    }

  //#endregion

  async componentDidMount () {
    document.body.classList.add('bg-grey-image');
    if(!this.props.inspection.reportChecked){
      this.props.getActiveInspection()
        .then(data => {
          this.props.setReportCheckFlag(true);
          setReportCheckFlag(true)
        })
        .catch(error => {
          console.log(error)
          this.props.setReportCheckFlag(true);
        });
    }

    this.checkForStoredInspections();
    this.props.updateUserDataRequest();

    await this.syncCertificates();
    // await this.checkForNewTerms();
  }

  componentWillUnmount () {
    document.body.classList.remove(... document.body.classList);
  }

  setEnabled(){
    return {disabled: false, active: false, complete:false};
  }

  setActive(){
    return {disabled: false, active: true, complete:false};
  }

  setCompleted(){
    return {disabled: false, active: false, complete:true};
  }

  setDisabled(){
    return { active: false, disabled: true, complete: false};
  }

  setActiveTab(tab){
    
    if(tab === this.props.startInspection.currentTab){
      return;
    }

    if(tab == 'companies'){
      this.props.setCompanyTab(this.setActive());
      this.props.setInspectionState({
        currentTab: 'companies',
        previous: false,
        next: false
      })

      this.selectCompany(this.props.startInspection.selectedCompany, false);
      return;
    }

    if(tab == 'locations' && !this.props.startInspection.locationTab.disabled){

      this.props.setCompanyTab(this.setCompleted());
      this.props.setLocationTab(this.setActive());
      this.props.setInspectionState({
        currentTab: 'locations',
        previous: false,
        next: false
      });

      if(this.props.startInspection.selectedSublocation){
        this.props.setInspectionState({
          currentTab: 'sublocations',
          previous: true
        });
        this.selectSublocation(this.props.startInspection.selectedSublocation, false);
        return;
      }

      if(this.props.startInspection.selectedLocation){
        this.selectLocation(this.props.startInspection.selectedLocation, false);
        return;
      }

      return;
    }

    if(tab == 'removals' && !this.props.startInspection.removalTab.disabled){

      this.props.setCompanyTab(this.setCompleted());
      this.props.setLocationTab(this.setCompleted());
      this.props.setRemovalTab(this.setActive());
      this.props.setInspectionState({
        currentTab: 'removals',
        previous: false,
        next: false
      });

      if(this.props.startInspection.selectedRemoval){
        this.selectRemoval(this.props.startInspection.selectedRemoval);
        return;
      }
    }
  }

  async selectCompany(item, goNext = true){
    this.props.setLocationTab({ ...this.setEnabled(), 'title': __('app.inspections.tab_locations')});
    this.props.setRemovalTab({ ...this.setDisabled(), 'title': __('app.inspections.tab_removals')});

    await localforage.setItem('bs_company', item);

    this.props.setCompanyTab({'title': item.name });
    this.props.setInspectionStateAsync({
      selectedCompany: item,
      selectedLocation: null,
      selectedSublocation: null,
      selectedRemoval: null,
      //next: true
    })
      .then(() => {
        goNext ? setTimeout(() => {this.nextStep();}, 200) : '';
      });
  }

  selectLocation(item, goNext = true){
    this.props.setRemovalTab({ ...this.setDisabled(), 'title': __('app.inspections.tab_removals') });
    this.props.setLocationTab({ 'title': item.name });
    this.props.setInspectionStateAsync({
      selectedLocation: item,
      selectedSublocation: null,
      selectedRemoval: null,
    })
      .then(() => {
        goNext ? setTimeout(() => {this.nextStep();}, 200) : '';
      });
  }

  selectSublocation(item, goNext = true){
    this.props.setRemovalTab({ ...this.setEnabled(), 'title': __('app.inspections.tab_removals') });
    this.props.setInspectionStateAsync({
      selectedSublocation: item,
      selectedRemoval: null,
    })
      .then(() => {
        goNext ? setTimeout(() => {this.nextStep();}, 200) : '';
      });
  }

  selectRemoval(item){
    this.props.setRemovalTab({ ...this.setCompleted(), 'title': item.name });
    this.props.setInspectionStateAsync({
      selectedRemoval: item,
      next: true
    })
      .then(() => this.removalSelectionRef.showModal());
  }

  onSwipeLeft(eventData){
    // Prevent swiping when modal is visible
    if(window.$('body').hasClass('modal-open')){
      return;
    }
    
    if(this.props.startInspection.currentTab == 'locations' && this.props.startInspection.selectedLocation) {
      this.nextStep();
      return;
    }

    return;
  }

  onSwipeRight(eventData){
    // Prevent swiping when modal is visible
    if(window.$('body').hasClass('modal-open')){
      return;
    }

    if(this.props.startInspection.currentTab != 'sublocations') {
      return;
    }

    this.previousStep();
  }

  nextStep(){
    if(this.props.startInspection.currentTab == 'companies' && this.props.startInspection.selectedCompany){
      this.setActiveTab('locations');
      return;
    }

    if(this.props.startInspection.currentTab == 'locations' && this.props.startInspection.selectedLocation) {
      this.props.setInspectionState({
        currentTab: 'sublocations',
        next: false,
        previous: true
      });
      return;
    }

    if(this.props.startInspection.currentTab == 'sublocations' && this.props.startInspection.selectedSublocation) {
      this.setActiveTab('removals');
      return;
    }

    if(this.props.startInspection.currentTab == 'removals' && this.props.startInspection.selectedRemoval) {
      this.removalSelectionRef.showModal();
      return;
    }
  }

  previousStep(){
    if(this.props.startInspection.currentTab == 'removals') {
      this.setActiveTab('locations');
      return
    }

    if(this.props.startInspection.currentTab == 'sublocations') {

      this.props.setInspectionState({
        currentTab: 'locations',
        previous: false
      });
      this.selectLocation(this.props.startInspection.selectedLocation, false);
      /*this.questionLeaveRightEnterLeft(() => {
        this.props.setInspectionState({
          currentTab: 'locations',
          previous: false
        });
        this.selectLocation(this.props.startInspection.selectedLocation, false);
      })*/
      return
    }

    if(this.props.startInspection.currentTab == 'locations') {
      this.setActiveTab('companies');
      return
    }

    return;
  }

  confirmModal(confirmModal) {
    // Parent stores the method that the child passed
    this.showModal = confirmModal;
  }

  checkForStoredInspections = () => {

    const { scheduledReports } = useScheduledReportStore.getState();

    if(scheduledReports.filter(report => report.status === reportStatus.COMPLETED_OFFLINE || report.status === reportStatus.STOPPED).length >= 1) {
      this.setState({ hasStoredInspections: true });
      console.log('Has stored inspections:', this.state.hasStoredInspections, window.navigator.onLine);
      return;
    }

    this.setState({ hasStoredInspections: false });
  }

  render () {

    let tabComponent;

    switch(this.props.startInspection.currentTab) {
      case 'companies': {
        tabComponent = (
          <CompanySelect
            onSelected={this.selectCompany}
            currentItem={this.props.startInspection.selectedCompany}
          />
        );
        break;
      }
      case 'locations': {
        tabComponent = (
          <LocationSelect
            onSelected={this.selectLocation}
            currentItem={this.props.startInspection.selectedLocation}
            selectedCompany={this.props.startInspection.selectedCompany}
          />
        );
        break;
      }
      case 'sublocations': {
        tabComponent = (
          <SublocationSelect 
            onSelected={this.selectSublocation} 
            currentItem={this.props.startInspection.selectedSublocation} 
            selectedLocation={this.props.startInspection.selectedLocation}
          />
        );
        break;
      }
      case 'removals': {
        tabComponent = (
          <RemovalSelect
            onRef={ref => (this.removalSelectionRef = ref)}
            onSelected={this.selectRemoval}
            currentItem={this.props.startInspection.selectedRemoval}
            data={{
              selectedCompany: this.props.startInspection.selectedCompany, 
              selectedLocation: this.props.startInspection.selectedLocation, 
              selectedSublocation: this.props.startInspection.selectedSublocation, 
              selectedRemoval: this.props.startInspection.selectedRemoval
            }}
          />
        );
        break;
      }
      default: {
        tabComponent = null;
        break;
      }
    }

    if(this.props.inspection.report){
      return <Redirect to={{ pathname: '/report' }} />
    }

    if(!this.props.inspection.reportChecked){
      return (
        <PageLoader text={ __('app.inspections.checking_report_loader') } />
      )
    }

    return (
      <DocumentTitle title={`${__('app.navigation.inspections')} - ${window.App.name}`}>
        <Header title={__('app.navigation.inspections')} />
        
        {/* Main container with content */}
        <div className="container mt-sm-3">
          
          {/* Top-level tabs indicating progress */}
          <div className="flex-row tab-bar d-flex justify-content-space-between">
            <Tab {... this.props.startInspection.companyTab} callback={() => this.setActiveTab('companies')} />
            <Tab {... this.props.startInspection.locationTab} callback={() => this.setActiveTab('locations')} />
            <Tab {... this.props.startInspection.removalTab} callback={() => this.setActiveTab('removals')} />
          </div>
          
          <div className="row mt-5">
            
            {/* Left icon to go back */}
            {this.props.startInspection.previous && (
              <div onClick={() => this.previousStep()} className="col-md-2 my-auto text-center md-navigate d-md-block d-none">
                <i className="fal fa-chevron-left"></i>
              </div>
            )}

            <div className={"col-md-8 col-12" + (!this.props.startInspection.previous && ' offset-md-2')}>
              <Swipeable 
                onSwipedLeft={(eventData) => this.onSwipeLeft(eventData)} 
                onSwipedRight={(eventData) => this.onSwipeRight(eventData) } 
                preventDefaultTouchmoveEvent={false}
                trackMouse={false} 
                className={`swipe-container ${this.state.animate ? 'sliding' : ''} ${this.state.leaveRight ? 'slide-right' : ''} ${this.state.leaveLeft ? 'slide-left' : ''}`}
              >
                {!window.navigator.onLine && (
                  <div className="alert alert-warning alert-dismissible mb-4" role="alert">
                    <i className="far fa-exclamation-triangle mr-2 font-size-22"></i>
                    <b className="font-size-22">Offline</b> <br />
                    { __('app.toasts.warning_currently_offline') }
                  </div>
                )}

                {(window.navigator.onLine && this.state.hasStoredInspections) && (
                  <div className="alert alert-warning alert-dismissible mb-4" role="alert">
                    <i className="far fa-exclamation-triangle mr-2 font-size-22"></i>
                    <b className="font-size-22">{ __('app.toasts.warning_local_not_synced_title') }</b> <br/>

                    { __('app.toasts.warning_local_not_synced_message') }

                    <br/> 
                    <button type="button" className="mt-4 button-brand-small" onClick={this.syncOfflineReports}>
                      { __('app.toasts.warning_local_not_synced_button')}
                    </button>
                  </div>
                )}

                {tabComponent}
              </Swipeable>
            </div>

            {/* Right icon to go forward */}
            {this.props.startInspection.next && (
              <div onClick={() => this.nextStep()} className="col-md-2 my-auto text-center md-navigate d-md-block d-none">
                <i className="fal fa-chevron-right"></i>
              </div>
            )}

          </div>

        </div>

        {/* Popup for accepting terms & conditions */}
        {this.state.newTermsToBeAccepted && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-isolate tw-bg-neutral-900/50 tw-z-[1000]">
            <div className="tw-p-6 tw-bg-white tw-rounded-lg tw-shadow-md tw-mx-4 tw-w-full tw-max-w-[30rem] tw-z-10">
              <h1 className="tw-text-2xl tw-font-semibold">
                {this.state.newTermsToBeAccepted.title}
              </h1>

              <p className="tw-text-md tw-mt-2 tw-text-neutral-500">
                {__('app.terms.description')}
              </p>

              <a 
                href={this.state.newTermsToBeAccepted.url} 
                className="mt-4 hover:tw-underline tw-text-md tw-text-black tw-font-semibold"
                target="_blank"
              >
                {__('app.terms.read_terms')}
                <SquareArrowOutUpRightIcon className='tw-inline-block tw-size-4 tw-ml-2 tw-relative tw-bottom-[1.5px]'/>
              </a>

              <div className="tw-mt-8 tw-flex tw-gap-x-6">
                <button 
                  className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-neutral-200 tw-text-neutral-900 tw-text-lg tw-font-semibold hover:tw-bg-neutral-300 tw-w-full"
                  onClick={() => this.rejectNewTerms()}
                >
                  {__('app.terms.decline')}
                </button>
                <button 
                  className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-neutral-900 tw-text-white tw-text-lg tw-font-semibold hover:tw-bg-neutral-800 tw-w-full" 
                  onClick={() => this.acceptNewTerms()}
                >
                  {__('app.terms.accept')}
                </button>
              </div>
            </div>
          </div>
        )}

      </DocumentTitle>
    );
  }

}

Inspection.propTypes = propTypes;

const mapStateToProps = ({inspection, startInspection}) => ({ inspection, startInspection });

const mapDispatchToProps = {
  getActiveInspection,
  setReportCheckFlag,
  setCompanyTab,
  setLocationTab,
  setRemovalTab,
  setInspectionState,
  setInspectionStateAsync,
  resetInspectionTabTranslations,
  updateUserDataRequest,
  logoutUser
};


export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Inspection));
